import React, { useEffect, useState } from 'react';
import { useToast } from '../components/ToastContext';
import { FaEdit, FaTrash, FaEye, FaPlus } from 'react-icons/fa';
import API_BASE_URL from '../config/api';

interface User {
  id: number;
  username?: string;
  password: string;
  pin?: string;
  role: 'client' | 'admin' | 'operator';
  first_name?: string;
  last_name?: string;
  email?: string;
  address?: string;
  contact?: string;
  status?: 'active' | 'inactive' | 'complete';
  spouse_detail?: string;
  dob?: string;
  evaluation_id?: number;
  created_at: string;
  assigned_to?: string;
  assigned_users?: string;
  active_status: number;
  assigned_status: number;
}

const defaultUser: Partial<User> = {
  username: '',
  password: '',
  pin: '',
  role: 'operator',
  first_name: '',
  last_name: '',
  email: '',
  address: '',
  contact: '',
  status: 'active',
  spouse_detail: '',
  dob: '',
  evaluation_id: 0,
  created_at: '',
  assigned_to: '',
  assigned_users: '',
  active_status: 1,
  assigned_status: 0,
};

const OperatorUsers: React.FC = () => {
  // Helper to get assigned user details (name and id)
  const getAssignedUserDetails = (assignedUsers: string | undefined) => {
    if (!assignedUsers) return null;
    const ids = assignedUsers.split(',').map(id => id.trim()).filter(Boolean);
    const details = ids.map(id => {
      const client = clients.find(c => c.id.toString() === id);
      return client ? `${client.first_name || ''} ${client.last_name || ''} (ID: ${client.id})` : `ID: ${id}`;
    });
    return details.length > 0 ? details : null;
  };
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalUser, setModalUser] = useState<Partial<User>>(defaultUser);
  const [showAssignedUsersModal, setShowAssignedUsersModal] = useState(false);
  const [assignedUsersList, setAssignedUsersList] = useState<string[]>([]);
  type ModalMode = 'create' | 'edit' | 'view';
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const isViewMode = modalMode === 'view';
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Fetch all users and filter clients for multi-select
  const fetchUsers = async () => {
    const res = await fetch(`${API_BASE_URL}/users`);
    const data = await res.json();
    setUsers(data.users.filter((u: User) => u.role === 'operator'));
    setClients(data.users.filter((u: User) => u.role === 'client'));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        showToast('Operator deleted successfully!', 'success');
        fetchUsers();
      } else {
        showToast(data.error || 'Failed to delete operator.', 'error');
      }
    } catch (err) {
      showToast('Error deleting operator.', 'error');
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const handleEdit = (user: User) => {
    setModalUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = (user: User) => {
    setModalUser(user);
    setModalMode('view');
    setShowModal(true);
  };

  const handleCreate = () => {
    setModalUser(defaultUser);
    setModalMode('create');
    setShowModal(true);
  };

  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setModalUser({ ...modalUser, [e.target.name]: e.target.value });
  };

  // Multi-select for assigned_users
  const handleAssignedUsersChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(opt => opt.value);
    setModalUser({
      ...modalUser,
      assigned_users: selectedOptions.join(','),
      assigned_status: selectedOptions.length > 0 ? 1 : 0
    });
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let res, data;
      if (modalMode === 'create') {
        res = await fetch(`${API_BASE_URL}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(modalUser),
        });
        data = await res.json();
        if (data.success) {
          showToast('Operator created successfully!', 'success');
        } else {
          showToast(data.error || 'Failed to create operator.', 'error');
        }
      } else if (modalMode === 'edit' && modalUser.id) {
        res = await fetch(`${API_BASE_URL}/users/${modalUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(modalUser),
        });
        data = await res.json();
        if (data.success) {
          showToast('Operator updated successfully!', 'success');
        } else {
          showToast(data.error || 'Failed to update operator.', 'error');
        }
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      showToast('Error saving operator.', 'error');
    }
  };

  // Helper to get assigned user names from assigned_users string
  const getAssignedUserNames = (assignedUsers: string | undefined) => {
    if (!assignedUsers) return [];
    const ids = assignedUsers.split(',').map(id => id.trim()).filter(Boolean);
    const names = ids.map(id => {
      const client = clients.find(c => c.id.toString() === id);
      return client ? `${client.first_name || ''} ${client.last_name || ''} (${client.email})` : id;
    });
    return names;
  };

  const filteredUsers = users.filter(u =>
    (u.username ?? '').toLowerCase().includes(filter.toLowerCase()) ||
    (u.first_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
    (u.last_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
    (u.email ?? '').toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <input
          className="border p-2 rounded w-1/3"
          placeholder="Filter by name, username, or email"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <button className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2" onClick={handleCreate}>
          <FaPlus /> Create Operator
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2">ID</th>
              <th className="px-4 py-2">Username</th>
              <th className="px-4 py-2">First Name</th>
              <th className="px-4 py-2">Last Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Assigned Users</th>
              <th className="px-4 py-2">Assigned Status</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className="border-t">
                <td className="px-4 py-2">{user.id}</td>
                <td className="px-4 py-2">{user.username}</td>
                <td className="px-4 py-2">{user.first_name}</td>
                <td className="px-4 py-2">{user.last_name}</td>
                <td className="px-4 py-2">{user.email}</td>
                <td className="px-4 py-2">
                  {user.assigned_users && user.assigned_users.split(',').filter(Boolean).length > 0 ? (
                    <button
                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded cursor-pointer"
                      onClick={() => {
                        setAssignedUsersList(getAssignedUserNames(user.assigned_users));
                        setShowAssignedUsersModal(true);
                      }}
                      title="View assigned users"
                    >
                      {user.assigned_users.split(',').filter(Boolean).length} Assigned
                    </button>
                  ) : (
                    <span className="text-gray-500">0 Assigned</span>
                  )}
                </td>
                <td className="px-4 py-2">{user.assigned_status === 1 ? 'Assigned' : 'Unassigned'}</td>
                <td className="px-4 py-2 flex gap-2">
                  <button className="text-blue-600" onClick={() => handleView(user)} title="View"><FaEye /></button>
                  <button className="text-green-600" onClick={() => handleEdit(user)} title="Edit"><FaEdit /></button>
                  <button className="text-red-600" onClick={() => { setDeleteId(user.id); setShowDeleteConfirm(true); }} title="Delete"><FaTrash /></button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={8} className="text-center py-4">No operators found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
  {/* Modal for Create/Edit/View */}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-xl font-bold"
              onClick={() => setShowDeleteConfirm(false)}
            >
              &times;
            </button>
            <h3 className="text-lg font-bold mb-4 text-center">Confirm Delete</h3>
            <p className="mb-6 text-center">Are you sure you want to delete this operator?</p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-red-600 text-white px-4 py-2 rounded font-semibold"
                onClick={() => deleteId !== null && handleDelete(deleteId)}
              >
                Yes, Delete
              </button>
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
  {showAssignedUsersModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md min-h-[300px] max-h-[500px] overflow-y-auto p-6 relative flex flex-col">
        <button
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-xl font-bold"
          onClick={() => setShowAssignedUsersModal(false)}
        >
          &times;
        </button>
        <h3 className="text-lg font-bold mb-4 text-center">Assigned Users</h3>
        {assignedUsersList.length > 0 ? (
          <ul className="list-disc ml-6">
            {assignedUsersList.map((name, idx) => (
              <li key={idx}>{name}</li>
            ))}
          </ul>
        ) : (
          <div className="text-gray-500 text-center">No users assigned.</div>
        )}
      </div>
    </div>
  )}
  {showModal && (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded text-left shadow-lg p-6 w-full max-w-md relative">
        <button className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold" onClick={() => setShowModal(false)}>&times;</button>
        <h2 className="text-xl font-bold mb-4">{modalMode === 'create' ? 'Create Operator' : modalMode === 'edit' ? 'Edit Operator' : 'View Operator'}</h2>
        {modalMode === 'view' ? (
          <div>
            <div className="mb-2">
              <label className="block font-semibold">Username</label>
              <div>{modalUser.username}</div>
            </div>
            <div className="mb-2">
              <label className="block font-semibold">First Name</label>
              <div>{modalUser.first_name}</div>
            </div>
            <div className="mb-2">
              <label className="block font-semibold">Last Name</label>
                  <div>{modalUser.last_name}</div>
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Email</label>
                  <div>{modalUser.email}</div>
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Role</label>
                  <div>{modalUser.role}</div>
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Status</label>
                  <div>{modalUser.status}</div>
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Assigned Users</label>
                  {getAssignedUserDetails(modalUser.assigned_users) ? (
                    <ul className="list-disc ml-6">
                      {getAssignedUserDetails(modalUser.assigned_users)?.map((detail, idx) => (
                        <li key={idx}>{detail}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500">Not assigned any user</div>
                  )}
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Created At</label>
                  <div>{modalUser.created_at}</div>
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Active Status</label>
                  <div>{modalUser.active_status === 1 ? 'Active' : 'Inactive'}</div>
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Assigned Status</label>
                  <div>{modalUser.assigned_status === 1 ? 'Assigned' : 'Unassigned'}</div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleModalSubmit}>
                <div className="mb-2">
                  <label className="block font-semibold">Username</label>
                  <input className="border p-2 rounded w-full" name="username" value={modalUser.username || ''} onChange={handleModalChange} disabled={isViewMode} required />
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">First Name</label>
                  <input className="border p-2 rounded w-full" name="first_name" value={modalUser.first_name || ''} onChange={handleModalChange} disabled={isViewMode} required />
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Last Name</label>
                  <input className="border p-2 rounded w-full" name="last_name" value={modalUser.last_name || ''} onChange={handleModalChange} disabled={isViewMode} required />
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Email</label>
                  <input className="border p-2 rounded w-full" name="email" value={modalUser.email || ''} onChange={handleModalChange} disabled={isViewMode} required />
                </div>
                <div className="mb-2">
                  <label className="block font-semibold">Assigned Users (Clients)</label>
                  <select
                    multiple
                    className="border p-2 rounded w-full"
                    name="assigned_users"
                    value={modalUser.assigned_users ? modalUser.assigned_users.split(',') : []}
                    onChange={handleAssignedUsersChange}
                    disabled={isViewMode}
                    required
                  >
                    {clients.map(client => (
                      <option key={client.id} value={client.id.toString()}>
                        {client.first_name} {client.last_name} ({client.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="block font-semibold">Password</label>
                  <input
                    className="border p-2 rounded w-full"
                    name="password"
                    type="password"
                    value={modalUser.password || ''}
                    onChange={handleModalChange}
                    disabled={isViewMode}
                    required={modalMode === 'create'}
                  />
                </div>
                {(modalMode === 'edit' || modalMode === 'create') && (
                  <button className="bg-blue-600 text-white px-4 py-2 rounded mt-4" type="submit">{modalMode === 'create' ? 'Create' : 'Update'}</button>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorUsers;
